import axios from 'axios';
import { PolicyErrorCodesEnum } from '@general/js/enums';
import { DHLError, axiosRequest, handleHttpResponse, getCurrentDate, SubmitPolicyError, SubmitPolicyInProcessError, SubmitPolicyTimeoutError } from '@general/js/utils';
import { EnvironmentVariables } from '@general/js/environmentVariables';
import AbstractClass from './MemberDetailsAbstractServices';
import { Services } from '../Services';

export default class MemberDetailsServices extends AbstractClass {
  /**
   * Get ref data of member details page
   * @returns {object}
   */
  static async getMemberDetailsPageReferenceData() {
    try {
      const response = await axios.get(Services.MEMBER_DETAILS.GET_MEMBER_DETAILS_REFERENCE_DATA);

      if (handleHttpResponse(response)) {
        // return member number string token swapped data
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getMemberDetailsPageReferenceData() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Get member detail data.
   * @param {string} requestingPersonCode person code of member who is requesting to get details
   * @param {string} accessingPersonCode person code of member details with this person code
   * @param {string} memberNumber membership id (policy number)
   * @returns {object}
   */
  static async getMemberDetails(accessingPersonCode, memberNumber) {
    try {
      const response = await axiosRequest().get(Services.MEMBER_DETAILS.GET_MEMBER_DETAILS(memberNumber, accessingPersonCode, getCurrentDate()));

      if (handleHttpResponse(response)) {
        const data = { ...response.data };

        return data;
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getMemberDetails() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * update member details data.
   * @param {object} memberDetails member details object
   * @param {string} memberNumber member number
   * @param {string} accessingPersonCode current accessing person code
   * @returns {object}
   */
  static async postMemberDetails(memberDetails, memberNumber, accessingPersonCode) {
    try {
      const response = await axiosRequest().patch(
        Services.MEMBER_DETAILS.PATCH_MEMBER_DETAILS(memberNumber, accessingPersonCode),
        { ...memberDetails },
        { timeout: Number(EnvironmentVariables.MemberPortalTimeout.TIMEOUT) },
      );
      return handleHttpResponse(response);
    } catch (error) {
      const policyErrorCode = error?.response?.data?.Error?.Code;
      if (policyErrorCode === PolicyErrorCodesEnum.SUBMIT_POLICY_ERROR) {
        throw new SubmitPolicyError(`postMemberDetails() - call failed with error: ${JSON.stringify(error.response.data.Error.ErrorMessage)}`, error.response.data.Error);
      } else if (error.code === PolicyErrorCodesEnum.TIMEOUT_ERROR) { // error.code is set by axios upon timeout
        throw new SubmitPolicyTimeoutError(`postMemberDetails() - call failed with error: ${JSON.stringify(error.message)}`, error);
      } else if (policyErrorCode === PolicyErrorCodesEnum.SUBMIT_POLICY_IN_PROCESS_ERROR) {
        throw new SubmitPolicyInProcessError(`postMemberDetails() - call failed with error: ${JSON.stringify(error.response.data.Error.ErrorMessage)}`, error.response.data.Error);
      }
      throw new DHLError(`postMemberDetails() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  static async getMemberships() {
    try {
      const response = await axiosRequest().get(Services.MEMBERSHIP_DETAILS.GET_MEMBERSHIPS);

      if (handleHttpResponse(response) && response.data.productList && response.data.productList.length > 0) {
        return response.data;
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getMemberDetails() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Get member selection contents.
   * @returns {object}
   */
  static async getMemberSelectionContents() {
    try {
      const response = await axios.get(Services.MEMBER_DETAILS.GET_MEMBER_SELECTION_CONTENTS);

      if (handleHttpResponse(response)) {
        // return member number string token swapped data
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getMemberSelectionContents() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Post call to get list of members on given membership
   * @param {string} memberNumber membership id (policy number)
   */
  static async getMembers(memberNumber) {
    try {
      const response = await axiosRequest().post(Services.MEMBER_DETAILS.POST_GET_MEMBERS, { memberNumber });

      if (handleHttpResponse(response) && response.data.memberList) {
        return response.data.memberList;
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getMembers() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Get reference data for no impact page
   * @returns {object}
   */
  static async getNoImpactPageReferenceData() {
    try {
      const response = await axios.get(Services.MEMBER_DETAILS.GET_NO_IMPACT_CONTENTS);

      if (handleHttpResponse(response)) {
        // return member number string token swapped data
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getNoImpactPageReferenceData() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Get reference data for premium impact page
   * @returns {object}
   */
  static async getPremiumImpactPageReferenceData() {
    try {
      const response = await axios.get(Services.MEMBER_DETAILS.GET_PREMIUM_IMPACT_CONTENTS);

      if (handleHttpResponse(response)) {
        // return member number string token swapped data
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getPremiumImpactReferenceData() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Get cover details for a given membership on the current date with productAndExcessDetailsOnly = false
   * @param {string} memberNumber Member number of current policy
   * @returns {object}
   */
  static async getCoverDetails(memberNumber) {
    try {
      const response = await axiosRequest().get(Services.MY_COVER.CHANGE_COVER.RETRIEVE_COVER_DETAILS(memberNumber, getCurrentDate(), false));

      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`getCoverDetails() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Fetches all the policies on the accessing person. 
   * It is used to check if a person has multiple policies in CRM. 
   * The requesting person needs to be on the requesting policy.
   * @param {string} memberNumber membership id (policy number)
   * @param {string} accessingPersonCode
   * @returns {object}
   */
  static async getMemberPolicies(memberNumber, accessingPersonCode) {
    try {
      const response = await axiosRequest().get(Services.COMMON.GET_MEMBER_POLICIES(memberNumber, accessingPersonCode, getCurrentDate()));

      if (handleHttpResponse(response)) {
        const data = { ...response.data };

        return data;
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getMemberPolicies() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }
}
